import { PhotoSubjectType } from '@common/interfaces/photo'
import ProfilePhoto, { ProfilePhotoSize } from '../ProfilePhoto'
import { PropsFromRedux } from './index'

export interface IProps extends PropsFromRedux {
  userId: string
  /** Already-resolved display name — the initials and alt text come from it. */
  name: string | null | undefined
  /** A photo the caller already holds. Omit and ProfilePhoto fetches it. */
  photo?: string | null
  /** A column value to paint while the photo is fetched. The fetched photo still wins. */
  iconUrl?: string | null
  size?: ProfilePhotoSize
  className?: string
}

/**
 * A user's avatar with their presence dot.
 *
 * Connected rather than taking presence as props, because the surfaces that want
 * it are option lists built by plain functions — `buildAssigneeOptions` returns
 * `{ value, label, icon }` and has no hooks or store access, and threading
 * presence through it would change the signature for its seventeen callers. One
 * component that reads the store itself keeps those call sites unchanged.
 *
 * The status emoji used to ride on the avatar too and no longer does — it reads
 * as a smudge at the 20-24px these render at, and covers the face doing it. It
 * now sits beside the name, where it is text-sized and in reading order: see
 * `Common/AimStatus`. Presence stays here, because a dot is legible small and
 * belongs to the face rather than to the name.
 */
const UserPresenceAvatar = ({
  userId,
  name,
  photo,
  iconUrl,
  onlineUserIds,
  size = 'small',
  className,
}: IProps) => (
  <ProfilePhoto
    name={name}
    subjectType={PhotoSubjectType.Users}
    id={userId}
    photo={photo}
    iconUrl={iconUrl}
    isOnline={onlineUserIds.includes(userId)}
    size={size}
    className={className}
  />
)

export default UserPresenceAvatar
